/**
 * The entry view and the two page-list navs.
 *
 * The entry view (`…/docs/<key>`) is left to the products only KingCAD serves
 * (a desktop map, a DOF / LiDAR catalog): the tree nav on the left, the entry
 * article in the middle, the »Na tej strani« rail on the right. A WEB product
 * and a placed KingCAD layer never reach it — DocsPage redirects them to their
 * theme / folder page + section. An unknown key renders the web page-list nav
 * with no active row and a notice in place of the article.
 *
 * Both navs share one renderer (`renderTreeNav`): the collapsible KingCAD tree
 * (0584) and, since 2026-09-28, the flat list of web theme pages.
 */

import type { WikiEntry, WikiSection } from '../../../shared/types/layerWiki.js';
import { docsCopy, getDocsLanguage } from '../docsCopy.js';
import type { EditState } from '../docsEdit.js';
import { attr, esc, ICON_CHEVRON, ICON_EXTERNAL } from '../docsHtml.js';
import {
  buildComponents,
  buildFacts,
  buildLinks,
  buildRail,
  buildRelations,
  hasDescription,
  surfaceChip,
} from '../docsFacts.js';
import {
  buildCadNav,
  buildWebNav,
  cadNeighbours,
  linkFor,
  navHitCount,
  navLabel,
  neighbours,
  parentOf,
  cadFolderLabel,
  type CadNavActive,
  type DocsCatalogue,
  type TreeNavRow,
} from '../docsModel.js';
import { docsPath, entryPath } from '../docsRouter.js';
import { bilingual } from '../docsText.js';
import { chipDate, chipText } from '../docsTime.js';
import { renderEditForm, renderEditRow, renderHistory } from './renderEdit.js';
import { renderAltTitle, renderDescription } from './renderSection.js';

export interface KatalogState {
  /** The requested key; null only for the /docs/katalog alias before it resolves. */
  key: string | null;
  query: string;
  /** Narrow layout: the nav list is open. */
  navOpen: boolean;
  /** Which nav + article shape: the web catalogue or the KingCAD tree. */
  section: WikiSection;
  /** KingCAD nav: the folder ids that are expanded. */
  openFolders: ReadonlySet<string>;
  /** King-admin editing (0569); absent or `isKingAdmin: false` renders the public page. */
  edit?: EditState;
}

/** The one chip: green, »Posodobljeno … nazaj«. */
function chip(label: string): string {
  return `<span class="docs-chip">${esc(label)}</span>`;
}

function renderEmptySearch(query: string): string {
  return `<div class="docs-nav-empty">${esc(docsCopy.emptySearch(query.trim()))}
          <button type="button" class="docs-nav-clear" data-docs-clear>${esc(docsCopy.clearSearch)}</button>
        </div>`;
}

interface TreeNavOptions {
  query: string;
  navOpen: boolean;
  navLabel: string;
  navToggle: (n: number) => string;
  /** The count shown while not searching (the section's layers), since collapsed folders hide rows. */
  total: number;
  /** The attribute a page row carries with its id (`data-docs-cadpage` / `data-docs-webpage`). */
  pageAttr: string;
  /** aria-label of a container folder's disclosure button. */
  folderToggle: (label: string) => string;
  /** The id prefix of a container's `role=group` (`cadnav-`). */
  groupPrefix: string;
}

/**
 * A page-list nav shaped like a docs sidebar: a native `<button aria-expanded>`
 * per container folder (root folders bold, sub-folders lighter), its children
 * in a NESTED `role="group"` that exists only while open and draws the guide
 * line + indent. A PAGE (a folder that holds layers, a web theme) is a link row
 * with its layer count, always expanded when it has pages nested; layers are
 * not rows — the page lists them. The »Rastri in LiDAR« products stay leaf
 * links. While searching the tree drops and the hits list flat
 * (`docs-nav--flat`): pages, then layers with their page's name.
 */
function renderTreeNav(rows: readonly TreeNavRow[], o: TreeNavOptions): string {
  const searching = o.query.trim() !== '';
  const n = searching ? navHitCount(rows) : o.total;
  const parts: string[] = [];
  const openGroups: number[] = [];
  const closeGroupsTo = (depth: number): void => {
    while (openGroups.length > 0 && (openGroups[openGroups.length - 1] as number) >= depth) {
      openGroups.pop();
      parts.push('</div>');
    }
  };
  for (const r of rows) {
    closeGroupsTo(r.depth);
    if (r.kind === 'folder') {
      if (r.href !== null) {
        // A page: a link, current when shown; its nested pages follow in a group.
        const cls = [
          'docs-nav-row',
          'docs-nav-leaf',
          'docs-nav-page',
          r.depth === 0 ? 'docs-nav-page--root' : '',
          r.onPath ? 'docs-nav-folder--path' : '',
        ]
          .filter(Boolean)
          .join(' ');
        parts.push(`<div class="docs-nav-folder">
          <a class="${cls}" href="${esc(r.href)}" data-docs-link ${o.pageAttr}="${esc(r.id)}" title="${esc(fullLabel(r))}"${r.active ? ' aria-current="page"' : ''}>
            <span class="docs-nav-row-label">${esc(r.label)}${subLine(r.sub)}</span>
            <span class="docs-nav-folder-n" aria-hidden="true">${r.count}</span>
          </a>
        </div>`);
        if (!searching) {
          parts.push(`<div class="docs-nav-tree-group docs-nav-tree-group--page" role="group">`);
          openGroups.push(r.depth);
        }
        continue;
      }
      const cls = [
        'docs-nav-folder-btn',
        r.depth === 0 ? 'docs-nav-folder-btn--root' : 'docs-nav-folder-btn--sub',
        r.onPath ? 'docs-nav-folder--path' : '',
      ]
        .filter(Boolean)
        .join(' ');
      parts.push(`<div class="docs-nav-folder">
          <button type="button" class="${cls}" data-docs-folder-toggle="${esc(r.id)}" aria-expanded="${r.open ? 'true' : 'false'}" aria-controls="${o.groupPrefix}${esc(r.id)}" aria-label="${esc(o.folderToggle(fullLabel(r)))}">
            ${ICON_CHEVRON}
            <span class="docs-nav-row-label">${esc(r.label)}${subLine(r.sub)}</span>
            <span class="docs-nav-folder-n" aria-hidden="true">${r.count}</span>
          </button>
        </div>`);
      if (r.open) {
        parts.push(
          `<div id="${o.groupPrefix}${esc(r.id)}" class="docs-nav-tree-group" role="group">`
        );
        openGroups.push(r.depth);
      }
      continue;
    }
    // A search hit: a layer linked to its section (page name alongside), or a product row.
    const sub = r.folderName ? `<span class="docs-nav-row-sub">${esc(r.folderName)}</span>` : '';
    const title = r.key === null ? `${r.title} · ${docsCopy.cad.noEntry}` : r.title;
    parts.push(`<a class="docs-nav-row docs-nav-leaf${r.key === null ? ' docs-nav-row--missing' : ''}" href="${esc(r.href)}" data-docs-link${r.key ? ` data-docs-key="${esc(r.key)}"` : ''} title="${esc(title)}"${r.active ? ' aria-current="page"' : ''}>
        <span class="docs-nav-row-label">${esc(r.label)}${sub}</span>
      </a>`);
  }
  closeGroupsTo(0);
  const empty = rows.length === 0 ? renderEmptySearch(o.query) : '';
  // A page with nothing nested opened an empty group above — drop it.
  const list = parts.join('').replace(EMPTY_PAGE_GROUP, '');
  return `
    <nav class="docs-nav docs-nav--tree${searching ? ' docs-nav--flat' : ''}" aria-label="${esc(o.navLabel)}">
      <button type="button" class="docs-nav-toggle" data-docs-nav-toggle aria-expanded="${o.navOpen ? 'true' : 'false'}" aria-controls="docs-nav-list">${esc(o.navToggle(n))}</button>
      <div id="docs-nav-list" class="docs-nav-list"${o.navOpen ? '' : ' hidden'}>
        <div class="docs-nav-count">${esc(docsCopy.count(n))}</div>
        ${list}${empty}
      </div>
    </nav>`;
}

const EMPTY_PAGE_GROUP =
  /<div class="docs-nav-tree-group docs-nav-tree-group--page" role="group"><\/div>/g;

type FolderRow = Extract<TreeNavRow, { kind: 'folder' }>;

/** The folder's full name — »KN - Kataster nepremičnin - parcele, Geodetska uprava RS« — for titles and aria-labels. */
function fullLabel(r: FolderRow): string {
  return r.sub ? `${r.label}, ${r.sub}` : r.label;
}

/** The muted second line of a comma-split folder name (Teo 2026-09-28). */
function subLine(sub: string | null): string {
  return sub ? ` <span class="docs-nav-row-sub">${esc(sub)}</span>` : '';
}

/** The KingCAD tree nav (0584): container folders as disclosure buttons, folder pages as link rows. */
export function renderCadNav(
  cat: DocsCatalogue,
  state: Pick<KatalogState, 'query' | 'navOpen' | 'openFolders'>,
  active: CadNavActive
): string {
  const c = docsCopy.cad;
  return renderTreeNav(buildCadNav(cat, state.query, active, state.openFolders), {
    query: state.query,
    navOpen: state.navOpen,
    navLabel: c.navLabel,
    navToggle: c.navToggle,
    total: cat.counts.cadLayers + cat.counts.cadRastri,
    pageAttr: 'data-docs-cadpage',
    folderToggle: c.folderToggle,
    groupPrefix: 'cadnav-',
  });
}

/** The web page-list nav (2026-09-28): one link row per theme page, the entries listed on the page. */
export function renderWebNav(
  cat: DocsCatalogue,
  state: Pick<KatalogState, 'query' | 'navOpen'>,
  activeSlug: string | null
): string {
  const c = docsCopy.web;
  return renderTreeNav(buildWebNav(cat, state.query, activeSlug), {
    query: state.query,
    navOpen: state.navOpen,
    navLabel: c.navLabel,
    navToggle: c.navToggle,
    total: cat.counts.layers,
    pageAttr: 'data-docs-webpage',
    folderToggle: (label) => label,
    groupPrefix: 'webnav-',
  });
}

function renderArticle(
  cat: DocsCatalogue,
  e: WikiEntry,
  edit: EditState | undefined,
  section: WikiSection
): string {
  const c = docsCopy;
  const admin = edit?.isKingAdmin === true;
  const editing = admin && edit.editingKey === e.key;
  const cad = section === 'cad';
  const parent = parentOf(cat, e);
  const text = bilingual(e, getDocsLanguage());
  const facts = buildFacts(e, parent, section);
  const links = buildLinks(e);
  const comps = buildComponents(e, cat.generatedAt);
  // The CAD tab already says which product serves the entry — no surface chip there.
  const surface = cad ? null : surfaceChip(e, parent);
  const relations = buildRelations(cat, e);
  const { prev, next } = cad ? cadNeighbours(cat, e.key) : neighbours(cat, e.key);

  // A web product links to its section on its theme page, a KingCAD layer to its folder page section.
  const entryLink = (x: WikiEntry, cls: string): string =>
    `<a class="${cls}" href="${esc(linkFor(cat, x.key))}" data-docs-link title="${esc(x.title)}">${esc(navLabel(x))}</a>`;

  const parentHtml = relations.parent
    ? `<div class="docs-parent">${esc(c.related.parent[relations.parent.kind])}: ${entryLink(relations.parent.entry, 'docs-parent-link')}</div>`
    : '';

  const relatedHtml =
    relations.children.length > 0
      ? `<section id="sec-povezani" class="docs-section">
          <h2 class="docs-section-title">${esc(c.related.heading)}</h2>
          ${relations.children
            .map(
              (g) => `<div class="docs-related-group">
                <div class="docs-related-label">${esc(c.related.children[g.kind])} · ${g.entries.length}</div>
                <ul class="docs-pills" role="list">${g.entries.map((x) => `<li>${entryLink(x, 'docs-pill')}</li>`).join('')}</ul>
              </div>`
            )
            .join('')}
        </section>`
      : '';

  // No text yet → the notice under the same anchor (the KingCAD layers without a desktop text);
  // a king admin gets the edit button in both cases (there may be Czech text to add too).
  const descHtml = editing
    ? renderEditForm(e, edit)
    : `<div id="sec-opis" class="docs-opis">
        ${hasDescription(e) ? renderDescription(text) : `<p class="docs-desc docs-desc--empty">${esc(c.noDescription)}</p>`}
        ${admin ? renderEditRow(e) : ''}
      </div>`;

  const historyHtml = admin ? renderHistory(edit, e.key) : '';

  const factsHtml = `
      <dl id="sec-podatki" class="docs-facts">
        ${facts
          .map(
            (f) =>
              `<div class="docs-fact"><dt>${esc(f.label)}</dt><dd${attr('title', f.title)}>${
                f.href
                  ? f.internal
                    ? `<a href="${esc(f.href)}" data-docs-link>${esc(f.value)}</a>`
                    : `<a href="${esc(f.href)}" target="_blank" rel="noopener noreferrer">${esc(f.value)}</a>`
                  : esc(f.value)
              }</dd></div>`
          )
          .join('')}
      </dl>`;

  const linksHtml =
    links.length > 0
      ? `<section id="sec-povezave" class="docs-section">
          <h2 class="docs-section-title">${esc(c.links.heading)}</h2>
          <ul class="docs-links" role="list">
            ${links
              .map(
                (l) =>
                  `<li><a class="docs-link" href="${esc(l.url)}" target="_blank" rel="noopener noreferrer">${esc(l.label)} ${ICON_EXTERNAL}</a></li>`
              )
              .join('')}
          </ul>
        </section>`
      : '';

  const compsHtml =
    comps.length >= 2
      ? `<section id="sec-sestavni" class="docs-section">
          <h2 class="docs-section-title">${esc(c.componentsHeading)}</h2>
          <ul class="docs-comps" role="list">
            ${comps
              .map(
                (x) =>
                  `<li class="docs-comp"><span class="docs-comp-label">${esc(x.label)}</span>${
                    x.meta ? `<span class="docs-comp-meta">${esc(x.meta)}</span>` : ''
                  }${chip(x.chip)}</li>`
              )
              .join('')}
          </ul>
        </section>`
      : '';

  const pager = `
      <nav class="docs-pager" aria-label="${esc(c.katalog.prev)} / ${esc(c.katalog.next)}">
        ${
          prev
            ? `<a class="docs-pager-btn" href="${esc(linkFor(cat, prev.key))}" data-docs-link rel="prev"><span class="docs-pager-dir">${esc(c.katalog.prev)}</span><span class="docs-pager-label">${esc(navLabel(prev))}</span></a>`
            : '<span></span>'
        }
        ${
          next
            ? `<a class="docs-pager-btn docs-pager-btn--next" href="${esc(linkFor(cat, next.key))}" data-docs-link rel="next"><span class="docs-pager-dir">${esc(c.katalog.next)}</span><span class="docs-pager-label">${esc(navLabel(next))}</span></a>`
            : ''
        }
      </nav>`;

  // Crumbs: the theme for a web entry; »King.Prostor CAD / folder / sub-folder« (labels without their publisher) for a desktop product.
  const crumbTail = cad
    ? `<a class="docs-crumb-link" href="${esc(docsPath({ view: 'cad' }))}" data-docs-link>${esc(c.cad.crumb)}</a>${(
        cat.cadIndex.get(e.key)?.folderIds ?? []
      )
        .map((id) => {
          const folder = cat.cadFolderById.get(id);
          const label = folder ? cadFolderLabel(cat, folder).label : id;
          return `<span aria-hidden="true">/</span><span class="docs-crumb-theme">${esc(label)}</span>`;
        })
        .join('')}`
    : `<a class="docs-crumb-link" href="${esc(docsPath({ view: 'web' }))}" data-docs-link>${esc(c.web.crumb)}</a><span aria-hidden="true">/</span><span class="docs-crumb-theme">${esc(e.theme)}</span>`;

  return `
    <article class="docs-article">
      <div class="docs-crumbs">
        <a class="docs-crumb-link" href="${esc(docsPath({ view: 'pregled' }))}" data-docs-link>${esc(c.katalog.crumbRoot)}</a>
        <span aria-hidden="true">/</span>${crumbTail}
      </div>
      <h1 class="docs-entry-title" tabindex="-1">${esc(text.title)}</h1>
      <div class="docs-chips">
        ${chip(chipText(chipDate(e), cat.generatedAt))}
        ${surface ? `<span class="docs-chip docs-chip--surface">${esc(surface)}</span>` : ''}
      </div>
      ${renderAltTitle(text)}
      ${parentHtml}
      ${descHtml}
      ${factsHtml}
      ${linksHtml}
      ${relatedHtml}
      ${compsHtml}
      ${historyHtml}
      ${pager}
    </article>`;
}

function renderAside(cat: DocsCatalogue, e: WikiEntry, admin: boolean): string {
  const c = docsCopy.katalog;
  const rail = buildRail(
    e,
    buildLinks(e),
    buildComponents(e, ''),
    buildRelations(cat, e).children.length > 0,
    admin
  );
  return `
    <aside class="docs-aside" aria-label="${esc(c.onThisPage)}">
      <div class="docs-aside-title">${esc(c.onThisPage)}</div>
      <div class="docs-rail">${rail.map((r) => `<a href="${esc(r.href)}">${esc(r.label)}</a>`).join('')}</div>
      <div class="docs-permalink">${esc(c.permalink)}<br><code>${esc(entryPath(e.key))}</code></div>
    </aside>`;
}

function renderNotFound(): string {
  return `
    <article class="docs-article">
      <div class="docs-crumbs">
        <a class="docs-crumb-link" href="${esc(docsPath({ view: 'pregled' }))}" data-docs-link>${esc(docsCopy.katalog.crumbRoot)}</a>
      </div>
      <h1 class="docs-entry-title docs-entry-title--notice" tabindex="-1">${esc(docsCopy.notFound)}</h1>
      <p class="docs-desc"><a href="${esc(docsPath({ view: 'web' }))}" data-docs-link>${esc(docsCopy.katalog.notFoundLink)}</a></p>
    </article>`;
}

export function renderKatalog(cat: DocsCatalogue, state: KatalogState): string {
  const entry = state.key ? (cat.byKey.get(state.key) ?? null) : null;
  const activeKey = entry?.key ?? null;
  const admin = state.edit?.isKingAdmin === true;
  const nav =
    state.section === 'cad'
      ? renderCadNav(cat, state, { pageId: null, key: activeKey })
      : renderWebNav(cat, state, entry ? (cat.webPageByKey.get(entry.key)?.slug ?? null) : null);
  return `
    <div class="docs-katalog docs-katalog--tree">
      ${nav}
      <div class="docs-katalog-body">
        ${entry ? renderArticle(cat, entry, state.edit, state.section) : renderNotFound()}
        ${entry ? renderAside(cat, entry, admin) : '<span></span>'}
      </div>
    </div>`;
}
